import { z } from 'zod';

// ─── Constants ──────────────────────────────────────────────────────────

export const IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const IMAGE_MAX_DIMENSION = 2048;
export const IMAGE_JPEG_QUALITY = 0.85;
export const IMAGE_ALLOWED_MIME = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
];

export const AUDIO_MAX_SECONDS = 60;
export const AUDIO_TARGET_SAMPLE_RATE = 16_000;
export const AUDIO_OUTPUT_MIME = 'audio/wav';

// ─── Types ──────────────────────────────────────────────────────────────

export const attachmentKindSchema = z.enum(['image', 'voice']);
export type AttachmentKind = z.infer<typeof attachmentKindSchema>;

export interface AttachmentDraft {
  id: string;
  kind: AttachmentKind;
  blob: Blob;
  mime: string;
  sizeBytes: number;
  previewUrl: string;
  width?: number;
  height?: number;
  durationMs?: number;
}

export interface AttachmentRecord {
  id: string;
  kind: AttachmentKind;
  url: string;
  mime: string;
  sizeBytes: number;
  width?: number;
  height?: number;
  durationMs?: number;
}

// ─── Errors ─────────────────────────────────────────────────────────────

export type MediaErrorCode =
  | 'image_type'
  | 'image_canvas'
  | 'image_decode'
  | 'image_encode'
  | 'image_too_large'
  | 'audio_unsupported'
  | 'audio_permission_denied'
  | 'audio_no_device'
  | 'audio_capture'
  | 'upload_cancelled';

export class MediaError extends Error {
  readonly code: MediaErrorCode;
  constructor(code: MediaErrorCode, message: string) {
    super(message);
    this.name = 'MediaError';
    this.code = code;
  }
}

// ─── Image: compress + EXIF strip via canvas re-encode ──────────────────

export async function processImageFile(file: File): Promise<AttachmentDraft> {
  const typeOk = IMAGE_ALLOWED_MIME.includes(file.type);
  const extOk = /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);
  if (!typeOk && !extOk) {
    throw new MediaError('image_type', 'Unsupported image type');
  }

  const bitmap = await loadBitmap(file);
  const { width, height } = fitToMaxDimension(
    bitmap.width,
    bitmap.height,
    IMAGE_MAX_DIMENSION
  );

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new MediaError('image_canvas', 'Could not create canvas context');
  ctx.drawImage(bitmap, 0, 0, width, height);

  let blob = await canvasToJpeg(canvas, IMAGE_JPEG_QUALITY);
  if (blob.size > IMAGE_MAX_BYTES) {
    blob = await canvasToJpeg(canvas, 0.7);
  }
  if (blob.size > IMAGE_MAX_BYTES) {
    throw new MediaError('image_too_large', 'Image is too large after compression');
  }

  return makeDraft('image', blob, { width, height });
}

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(file);
    } catch {
      // Fall through to <img> fallback
    }
  }
  return await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new MediaError('image_decode', 'Could not decode image'));
    };
    img.src = url;
  });
}

function fitToMaxDimension(
  w: number,
  h: number,
  max: number
): { width: number; height: number } {
  if (w <= max && h <= max) return { width: w, height: h };
  const ratio = w >= h ? max / w : max / h;
  return { width: Math.round(w * ratio), height: Math.round(h * ratio) };
}

function canvasToJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new MediaError('image_encode', 'Could not encode image'));
        else resolve(blob);
      },
      'image/jpeg',
      quality
    );
  });
}

// ─── Audio: capture via ScriptProcessorNode + 16kHz PCM WAV encode ──────
// ScriptProcessorNode is deprecated but reliable for short recordings;
// AudioWorklet has known intermittent-silence bugs on Chrome/macOS
// (per AUTAIMATE BUILD STANDARD v3 §16).

export interface AudioRecorderHandle {
  stop: () => Promise<AttachmentDraft>;
  cancel: () => void;
  getElapsedMs: () => number;
}

export async function startAudioRecorder(): Promise<AudioRecorderHandle> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new MediaError(
      'audio_unsupported',
      'Microphone is not supported on this device'
    );
  }

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
  } catch (err) {
    const name = err instanceof Error ? err.name : 'Unknown';
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
      throw new MediaError('audio_permission_denied', 'Microphone permission was denied');
    }
    if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
      throw new MediaError('audio_no_device', 'No microphone was found');
    }
    throw new MediaError('audio_capture', 'Could not start microphone');
  }

  const audioContext = new AudioContext();
  if (audioContext.state === 'suspended') await audioContext.resume();

  const sourceSampleRate = audioContext.sampleRate;
  const source = audioContext.createMediaStreamSource(stream);
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const processor = audioContext.createScriptProcessor(4096, 1, 1);
  const chunks: Float32Array[] = [];

  processor.onaudioprocess = (event) => {
    const input = event.inputBuffer.getChannelData(0);
    chunks.push(new Float32Array(input)); // copy — input buffer is reused
  };

  source.connect(processor);
  processor.connect(audioContext.destination); // required for callbacks

  const startedAt = performance.now();
  let stopped = false;

  const cleanup = () => {
    try {
      processor.disconnect();
    } catch {
      // ignore
    }
    try {
      source.disconnect();
    } catch {
      // ignore
    }
    try {
      void audioContext.close();
    } catch {
      // ignore
    }
    stream.getTracks().forEach((t) => t.stop());
  };

  return {
    getElapsedMs: () => performance.now() - startedAt,
    cancel: () => {
      if (stopped) return;
      stopped = true;
      cleanup();
    },
    stop: async () => {
      if (stopped) throw new MediaError('audio_capture', 'Recorder already stopped');
      stopped = true;

      const totalSamples = chunks.reduce((sum, c) => sum + c.length, 0);
      const merged = new Float32Array(totalSamples);
      let offset = 0;
      for (const c of chunks) {
        merged.set(c, offset);
        offset += c.length;
      }

      cleanup();

      const resampled = resampleLinear(
        merged,
        sourceSampleRate,
        AUDIO_TARGET_SAMPLE_RATE
      );
      const wav = encodeWav16(resampled, AUDIO_TARGET_SAMPLE_RATE);
      const blob = new Blob([wav], { type: AUDIO_OUTPUT_MIME });

      const durationMs = Math.round(
        (resampled.length / AUDIO_TARGET_SAMPLE_RATE) * 1000
      );
      return makeDraft('voice', blob, { durationMs });
    },
  };
}

function resampleLinear(
  input: Float32Array,
  fromRate: number,
  toRate: number
): Float32Array {
  if (fromRate === toRate) return input;
  const ratio = fromRate / toRate;
  const outLength = Math.floor(input.length / ratio);
  const out = new Float32Array(outLength);
  for (let i = 0; i < outLength; i++) {
    const src = i * ratio;
    const lo = Math.floor(src);
    const hi = Math.min(lo + 1, input.length - 1);
    const t = src - lo;
    const a = input[lo] ?? 0;
    const b = input[hi] ?? 0;
    out[i] = a * (1 - t) + b * t;
  }
  return out;
}

function encodeWav16(samples: Float32Array, sampleRate: number): ArrayBuffer {
  const byteRate = sampleRate * 2; // mono, 16-bit
  const dataLength = samples.length * 2;
  const buffer = new ArrayBuffer(44 + dataLength);
  const view = new DataView(buffer);

  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, 'data');
  view.setUint32(40, dataLength, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i++) {
    const raw = samples[i] ?? 0;
    const s = Math.max(-1, Math.min(1, raw));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }
  return buffer;
}

function writeString(view: DataView, offset: number, str: string): void {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────

function makeDraft(
  kind: AttachmentKind,
  blob: Blob,
  meta: { width?: number; height?: number; durationMs?: number }
): AttachmentDraft {
  const draft: AttachmentDraft = {
    id: cryptoRandomId(),
    kind,
    blob,
    mime: blob.type || (kind === 'image' ? 'image/jpeg' : AUDIO_OUTPUT_MIME),
    sizeBytes: blob.size,
    previewUrl: URL.createObjectURL(blob),
  };
  if (meta.width !== undefined) draft.width = meta.width;
  if (meta.height !== undefined) draft.height = meta.height;
  if (meta.durationMs !== undefined) draft.durationMs = meta.durationMs;
  return draft;
}

function cryptoRandomId(): string {
  if ('randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function revokeDraft(draft: AttachmentDraft): void {
  try {
    URL.revokeObjectURL(draft.previewUrl);
  } catch {
    // ignore
  }
}

// ─── Mock upload (real R2 signed-URL flow lands when API repo ships) ────

export interface UploadProgress {
  loaded: number;
  total: number;
}

export interface UploadHandle {
  promise: Promise<AttachmentRecord>;
  cancel: () => void;
}

export function uploadAttachment(
  draft: AttachmentDraft,
  onProgress?: (p: UploadProgress) => void
): UploadHandle {
  let cancelled = false;
  const total = draft.sizeBytes;
  const startedAt = performance.now();
  const targetMs = 1200 + Math.random() * 600;

  const promise = new Promise<AttachmentRecord>((resolve, reject) => {
    const tick = () => {
      if (cancelled) {
        reject(new MediaError('upload_cancelled', 'Upload cancelled'));
        return;
      }
      const elapsed = performance.now() - startedAt;
      const fraction = Math.min(1, elapsed / targetMs);
      onProgress?.({ loaded: Math.round(total * fraction), total });
      if (fraction >= 1) {
        const record: AttachmentRecord = {
          id: draft.id,
          kind: draft.kind,
          url: draft.previewUrl,
          mime: draft.mime,
          sizeBytes: draft.sizeBytes,
        };
        if (draft.width !== undefined) record.width = draft.width;
        if (draft.height !== undefined) record.height = draft.height;
        if (draft.durationMs !== undefined) record.durationMs = draft.durationMs;
        resolve(record);
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });

  return {
    promise,
    cancel: () => {
      cancelled = true;
    },
  };
}
